import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle2, Clock, PartyPopper, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SaleGraphic } from './SaleGraphic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { client_name: '', carrier: '', product_type: '', status: '', annual_premium: '', target_premium: '', lead_source: '' };

export const SubmitSaleModal = ({ open, onOpenChange, onSubmitted }) => {
  const { user, getAuthHeader } = useAuth();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || config) return;
    axios.get(`${API}/sales/config`, getAuthHeader())
      .then((res) => setConfig(res.data))
      .catch(() => toast.error('Failed to load sale options'));
  }, [open]);

  useEffect(() => {
    if (open) { setForm(EMPTY); setResult(null); }
  }, [open]);

  const isAnnuity = form.product_type === 'Annuity';
  const isIUL = form.product_type === 'IUL';
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toAmount = (v) => parseFloat(String(v).replace(/[^0-9.]/g, ''));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.carrier || !form.product_type || !form.status || !form.lead_source) {
      toast.error('Please complete every field');
      return;
    }
    const amount = toAmount(form.annual_premium);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const targetPremium = isIUL ? toAmount(form.target_premium) : null;
    if (isIUL && (!targetPremium || targetPremium <= 0)) {
      toast.error('Enter a valid target premium');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/sales/submit`,
        { ...form, client_name: form.client_name.trim(), annual_premium: amount, target_premium: targetPremium },
        getAuthHeader()
      );
      setResult(res.data.sale);
      toast.success('Sale submitted — you\'re on the board!');
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit sale');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[520px]" data-testid="submit-sale-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope'] text-xl font-extrabold text-[#2B3674]">
            {result ? <PartyPopper className="h-5 w-5 text-[#23ACD5]" /> : null}
            {result ? 'Sale logged!' : 'Submit a Sale'}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <p className="text-sm text-[#A3AED0]">
              Credited to today's board and the Friday–Thursday weekly leaderboard. Save the graphic below to share.
            </p>
            <SaleGraphic sale={result} agentName={user?.name} agentPicture={user?.profile_picture} />
            <button
              type="button"
              onClick={() => setResult(null)}
              className="w-full rounded-full border border-[#E9EDF7] px-6 py-3 text-sm font-bold text-[#2B3674] transition-colors hover:bg-[#F4F7FE]"
              data-testid="submit-another-sale-btn"
            >
              Submit another sale
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sale-client">Client name</Label>
              <Input
                id="sale-client"
                value={form.client_name}
                onChange={(e) => set('client_name')(e.target.value)}
                placeholder="John Smith"
                data-testid="sale-client-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Carrier</Label>
              <Select value={form.carrier} onValueChange={set('carrier')}>
                <SelectTrigger data-testid="sale-carrier-select">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(config?.carriers || []).map((c) => (
                    <SelectItem key={c} value={c} data-testid={`sale-carrier-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Product type</Label>
              <Select value={form.product_type} onValueChange={set('product_type')}>
                <SelectTrigger data-testid="sale-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {(config?.product_types || []).map((p) => (
                    <SelectItem key={p} value={p} data-testid={`sale-product-${p}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'approved', label: 'Approved', icon: CheckCircle2, color: '#01B574' },
                  { value: 'underwriting', label: 'In underwriting', icon: Clock, color: '#FF9000' },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = form.status === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('status')(opt.value)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-left text-sm font-bold transition-colors ${
                        active ? 'border-transparent text-white' : 'border-[#E9EDF7] text-[#2B3674] hover:bg-[#F4F7FE]'
                      }`}
                      style={active ? { backgroundColor: opt.color } : undefined}
                      data-testid={`sale-status-${opt.value}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sale-amount">{isAnnuity ? 'Rollover amount' : 'Annual premium'}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#A3AED0]">$</span>
                <Input
                  id="sale-amount"
                  inputMode="decimal"
                  className="pl-7"
                  value={form.annual_premium}
                  onChange={(e) => set('annual_premium')(e.target.value)}
                  placeholder={isAnnuity ? '250,000' : '1,800'}
                  data-testid="sale-amount-input"
                />
              </div>
            </div>

            {isIUL ? (
              <div className="space-y-1.5">
                <Label htmlFor="sale-target-premium">Target premium</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#A3AED0]">$</span>
                  <Input
                    id="sale-target-premium"
                    inputMode="decimal"
                    className="pl-7"
                    value={form.target_premium}
                    onChange={(e) => set('target_premium')(e.target.value)}
                    placeholder="1,200"
                    data-testid="sale-target-premium-input"
                  />
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label>Lead source</Label>
              <Select value={form.lead_source} onValueChange={set('lead_source')}>
                <SelectTrigger data-testid="sale-lead-source-select">
                  <SelectValue placeholder="Select lead source" />
                </SelectTrigger>
                <SelectContent>
                  {(config?.lead_sources || []).map((l) => (
                    <SelectItem key={l} value={l} data-testid={`sale-lead-source-${l.replace(/\s+/g, '-')}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] px-6 py-3.5 font-['Manrope'] text-sm font-extrabold text-[#06263A] shadow-[0_8px_28px_rgba(35,172,213,0.45)] transition-transform hover:scale-[1.01] disabled:opacity-60"
              data-testid="sale-submit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? 'Submitting…' : 'Submit Sale'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
